import type { Filter, Sort } from '@crm/contracts';
import type { ListApi } from '../components/boards/record-list.tsx';
import type { PipelineView, StageCountView } from './client.ts';
import { stageRefOf } from './record-data.ts';

/**
 * A deal or a job as a list shows it in ONE pipeline of its type: its own status in the default
 * pipeline, its place in an extra one (`deal_pipelines` / `job_pipelines`).
 */
interface Placed {
  id: string;
  stageId: string;
  stage: { id: string; name: string; terminalKind: 'won' | 'lost' | null };
  pipelines: Array<{
    pipelineId: string;
    isDefault: boolean;
    stageId: string;
    stage: { id: string; name: string; terminalKind: 'won' | 'lost' | null };
    archivedAt: string | null;
  }>;
}

/** The stage of `r` in the pipeline shown; undefined `pipelineId` is the default (its status). */
export function stageInPlace<T extends Placed>(r: T, pipelineId: string | undefined) {
  if (!pipelineId) return r.stage;
  return r.pipelines.find((m) => m.pipelineId === pipelineId && !m.archivedAt)?.stage ?? null;
}

/**
 * The list's search, counts and move scoped to one pipeline (unset = the default, every record).
 * A move is the record's `:move-status`, which moves the status for a default stage and only the
 * place for an extra one, so a drag on an extra board never changes the status.
 */
export function placeList<T extends Placed>(
  pipelineId: string | undefined,
  calls: {
    search: (body: {
      q?: string;
      filter?: Filter;
      sort?: Sort[];
      cursor?: string;
      limit: number;
      pipelineId?: string;
    }) => Promise<{ items: T[]; nextCursor: string | null }>;
    counts: (body: {
      q?: string;
      filter?: Filter;
      pipelineId?: string;
    }) => Promise<StageCountView[]>;
    move: (id: string, stageId: string) => Promise<unknown>;
    titleOf: (r: T) => string;
    sortable: string[];
  },
): ListApi<T> {
  const scoped = pipelineId ? { pipelineId } : {};
  return {
    search: (body) => calls.search({ ...body, ...scoped }),
    counts: (body) => calls.counts({ ...body, ...scoped }),
    move: (r, stageId) => calls.move(r.id, stageId),
    idOf: (r) => r.id,
    // A record not in this pipeline is in no column (the API never lists one).
    stageOf: (r) => stageInPlace(r, pipelineId)?.id ?? '',
    titleOf: calls.titleOf,
    moved: (r, stage) => {
      const ref = stageRefOf(stage);
      if (!pipelineId) {
        const pipelines = r.pipelines.map((m) =>
          m.isDefault ? { ...m, stageId: stage.id, stage: ref } : m,
        );
        return { ...r, stageId: stage.id, stage: ref, pipelines };
      }
      const pipelines = r.pipelines.map((m) =>
        m.pipelineId === pipelineId ? { ...m, stageId: stage.id, stage: ref } : m,
      );
      return { ...r, pipelines };
    },
    sortable: calls.sortable,
  };
}

/** The pipeline a list shows: the one picked, else (gone, archived, never picked) the default. */
export function pickedPipeline(
  pipelines: readonly PipelineView[],
  pipelineId: string | undefined,
): PipelineView | undefined {
  return pipelines.find((p) => p.id === pipelineId) ?? pipelines.find((p) => p.isDefault);
}
